import React, { Component } from 'react';
import RankChainEditor from './RankChainEditor';
import ScoreTableEditor from './ScoreTableEditor';
import { PRESETS, presetPorKey, describirEtapas } from '../utils/tournamentFormats';

// Alta de torneo. Vive acá y no dentro de una pantalla porque se entra por DOS lados: desde el
// listado de torneos (torneo suelto) y desde un evento (torneo enganchado a ese evento). El que
// llama decide qué endpoint usar; el formulario es el mismo, así que el operador ve lo mismo en
// los dos caminos.
//
// Arranca por el FORMATO: "Catan · clasificatorio oficial" rellena todo lo que el reglamento pide
// —puntaje 1/0 por tabla, la cadena de desempate, la mesa final, los tramos por cantidad de
// anotados— y deja todo editable. Antes esto sólo se podía hacer por API, y el alta desde la
// pantalla creaba el torneo SIN mesa final: cerrar la última ronda lo terminaba y coronaba al
// primero de la tabla de puntos.
//
// Overlay propio y no modal de Bootstrap: el backdrop de Bootstrap sobrevive a la navegación y
// deja la pantalla siguiente congelada.
class TournamentCreateModal extends Component {

    constructor(props) {
        super(props);
        const inicial = props.initial || {};
        this.state = {
            preset: 'enigma_clasico',
            title: inicial.title || '',
            ...presetPorKey('enigma_clasico').values,
            gameName: inicial.gameName || '',
            avanzado: false,
            error: null,
            saving: false,
        };
    }

    campo = (k, v) => this.setState({ [k]: v, error: null })

    elegirPreset = (key) => {
        const p = presetPorKey(key);
        if (!p) { this.setState({ preset: 'personalizado' }); return; }
        const values = { ...p.values };
        // El nombre del juego que ya tipeó el usuario no se pisa.
        if (this.state.gameName && values.gameName) delete values.gameName;
        this.setState({ preset: key, ...values, error: null });
    }

    // Cualquier cambio a mano después de elegir un preset lo convierte en "personalizado": el
    // preset es un punto de partida, no una etiqueta que promete algo que ya no es.
    tocar = (k, v) => {
        const p = presetPorKey(this.state.preset);
        // Los inputs devuelven texto: "3" y 3 son lo mismo para esto.
        const norm = (x) => (typeof x === 'string' && x.trim() !== '' && !Number.isNaN(Number(x)) ? Number(x) : x);
        const difiere = p && p.values[k] !== undefined && JSON.stringify(norm(p.values[k])) !== JSON.stringify(norm(v));
        this.setState({ [k]: v, preset: difiere ? 'personalizado' : this.state.preset, error: null });
    }

    crear = async () => {
        // Guarda de reentrada: `saving` deshabilita el BOTÓN, pero el Enter del campo Nombre lo
        // saltea. Dos Enter seguidos mandaban dos POST y creaban dos torneos.
        if (this.state.saving) return;
        const s = this.state;
        const title = String(s.title || '').trim();
        if (!title) { this.setState({ error: 'Poné un nombre para el torneo.' }); return; }
        const esLiga = s.sessionsPlanned != null && s.sessionsPlanned !== '';
        if (!esLiga && s.finalTableSize == null
            && !window.confirm('Sin mesa final, al cerrar la última ronda el torneo termina y gana el primero de la tabla. ¿Crear así?')) {
            return;
        }

        this.setState({ saving: true, error: null });
        try {
            await this.props.onCreate({
                preset: s.preset,
                title,
                gameName: String(s.gameName || '').trim() || null,
                tableSize: Number(s.tableSize),
                minTableSize: Number(s.minTableSize),
                roundsPlanned: Number(s.roundsPlanned),
                sessionsPlanned: esLiga ? Number(s.sessionsPlanned) : null,
                pairingMode: s.pairingMode,
                avoidRematch: !!s.avoidRematch,
                scoringMode: s.scoringMode,
                scoreMin: Number(s.scoreMin),
                scoreMax: Number(s.scoreMax),
                scoreTable: s.scoringMode === 'table' ? s.scoreTable : null,
                usesRawScore: !!s.usesRawScore,
                rankBy: s.rankBy,
                tiebreaker: s.tiebreaker || 'wins_then_raw',
                finalTableSize: esLiga ? null : (s.finalTableSize == null ? null : Number(s.finalTableSize)),
                bracketPlan: esLiga ? null : (s.bracketPlan || null),
            });
        } catch (e) {
            this.setState({ error: e.message, saving: false });
        }
    }

    render() {
        const s = this.state;
        const esLiga = s.sessionsPlanned != null && s.sessionsPlanned !== '';
        const preset = presetPorKey(s.preset);
        return (
            <div style={{
                position: 'fixed', top: 0, right: 0, bottom: 0, left: 0, background: 'rgba(15,23,42,.45)',
                zIndex: 1080, display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
                padding: '5vh 16px', overflowY: 'auto',
            }} onClick={this.props.onCancel}>
                <div className="card card-small" style={{ width: 620, maxWidth: '100%' }}
                    onClick={(e) => e.stopPropagation()}>
                    <div className="card-header border-bottom d-flex justify-content-between align-items-center">
                        <h6 className="m-0">{this.props.titulo || 'Nuevo torneo'}</h6>
                        <button type="button" className="btn btn-sm btn-white" onClick={this.props.onCancel}>Cancelar</button>
                    </div>
                    <div className="card-body">
                        {this.props.nota ? (
                            <div style={{
                                background: '#EEF4FF', borderRadius: '.25rem', padding: '8px 10px',
                                fontSize: 12.5, marginBottom: 14, lineHeight: 1.45,
                            }}>{this.props.nota}</div>
                        ) : null}

                        <div className="form-group">
                            <label>Formato</label>
                            <select className="form-control" value={s.preset} onChange={(e) => this.elegirPreset(e.target.value)}>
                                {PRESETS.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
                                <option value="personalizado">Personalizado</option>
                            </select>
                            {preset ? <small className="text-muted">{preset.resumen}</small> : null}
                        </div>

                        <div className="form-group">
                            <label>Nombre</label>
                            <input type="text" className="form-control" autoFocus value={s.title}
                                placeholder={s.preset === 'catan_oficial' ? 'Clasificatorio Catan — septiembre' : 'Liga de Carcassonne'}
                                onChange={(e) => this.campo('title', e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter') this.crear(); }} />
                        </div>
                        <div className="form-group">
                            <label>Juego</label>
                            <input type="text" className="form-control" value={s.gameName || ''}
                                placeholder="Carcassonne"
                                onChange={(e) => this.campo('gameName', e.target.value)} />
                        </div>
                        <div className="form-group">
                            <label>Tipo</label>
                            <select className="form-control" value={s.sessionsPlanned == null ? '' : String(s.sessionsPlanned)}
                                onChange={(e) => this.tocar('sessionsPlanned', e.target.value === '' ? null : Number(e.target.value))}>
                                <option value="">Torneo de un día</option>
                                {[4, 6, 8, 10, 12, 16].map((n) => (
                                    <option key={n} value={n}>Liga de {n} jornadas</option>
                                ))}
                            </select>
                            <small className="text-muted">
                                {esLiga
                                    ? 'Una liga se juega en varias fechas y la tabla general suma todo lo jugado. No tiene mesa final.'
                                    : 'Todas las rondas se juegan el mismo día.'}
                            </small>
                        </div>
                        <div className="row">
                            <div className="col-4">
                                <div className="form-group">
                                    <label>Jugadores por mesa</label>
                                    <select className="form-control" value={s.tableSize}
                                        onChange={(e) => this.tocar('tableSize', Number(e.target.value))}>
                                        {[2, 3, 4, 5, 6].map((n) => <option key={n} value={n}>{n}</option>)}
                                    </select>
                                </div>
                            </div>
                            <div className="col-4">
                                <div className="form-group">
                                    <label>Mínimo por mesa</label>
                                    <select className="form-control" value={s.minTableSize}
                                        onChange={(e) => this.tocar('minTableSize', Number(e.target.value))}>
                                        {[2, 3, 4, 5, 6].filter((n) => n <= Number(s.tableSize)).map((n) => <option key={n} value={n}>{n}</option>)}
                                    </select>
                                </div>
                            </div>
                            <div className="col-4">
                                <div className="form-group">
                                    <label>{esLiga ? 'Rondas por jornada' : 'Rondas'}</label>
                                    <input type="number" className="form-control" min="1" max="12"
                                        value={s.roundsPlanned}
                                        onChange={(e) => this.tocar('roundsPlanned', e.target.value)} />
                                </div>
                            </div>
                        </div>
                        {esLiga ? null : (
                            <div className="form-group">
                                <label>Mesa final</label>
                                <select className="form-control" value={s.finalTableSize == null ? '' : String(s.finalTableSize)}
                                    onChange={(e) => this.tocar('finalTableSize', e.target.value === '' ? null : Number(e.target.value))}>
                                    <option value="">Sin mesa final</option>
                                    {[3, 4, 5, 6].map((n) => <option key={n} value={n}>Los {n} mejores</option>)}
                                </select>
                                {s.finalTableSize == null ? (
                                    <small style={{ color: '#C4183C' }}>
                                        Sin mesa final, al cerrar la última ronda el torneo termina y gana el primero de la tabla.
                                    </small>
                                ) : null}
                            </div>
                        )}

                        <button type="button" className="btn btn-sm btn-link p-0 mb-3" onClick={() => this.setState({ avanzado: !s.avanzado })}>
                            {s.avanzado ? '▾' : '▸'} Puntaje, desempate y etapas
                        </button>
                        {s.avanzado ? (
                            <div style={{ borderLeft: '3px solid #E1E5EB', paddingLeft: 12, marginBottom: 14 }}>
                                <div className="form-group">
                                    <label>Puntaje</label>
                                    <select className="form-control" value={s.scoringMode}
                                        onChange={(e) => this.tocar('scoringMode', e.target.value)}>
                                        <option value="formula">Por puesto (fórmula lineal)</option>
                                        <option value="table">Por tabla (cuánto paga cada puesto)</option>
                                    </select>
                                </div>
                                {s.scoringMode === 'table' ? (
                                    <ScoreTableEditor value={s.scoreTable} min={s.minTableSize} max={s.tableSize}
                                        onChange={(v) => this.tocar('scoreTable', v)} />
                                ) : (
                                    <div className="row">
                                        <div className="col-6 form-group">
                                            <label>Gana la mesa</label>
                                            <input type="number" className="form-control" value={s.scoreMax}
                                                onChange={(e) => this.tocar('scoreMax', e.target.value)} />
                                        </div>
                                        <div className="col-6 form-group">
                                            <label>Sale último</label>
                                            <input type="number" className="form-control" value={s.scoreMin}
                                                onChange={(e) => this.tocar('scoreMin', e.target.value)} />
                                        </div>
                                    </div>
                                )}
                                <div className="form-group">
                                    <label className="d-flex align-items-center" style={{ gap: 8 }}>
                                        <input type="checkbox" checked={!!s.usesRawScore}
                                            onChange={(e) => this.tocar('usesRawScore', e.target.checked)} />
                                        Se anota el puntaje del juego (PV)
                                    </label>
                                </div>
                                <div className="form-group">
                                    <label>Orden de la tabla</label>
                                    <RankChainEditor value={s.rankBy} onChange={(v) => this.tocar('rankBy', v)} />
                                </div>
                                {esLiga ? null : (
                                    <div className="form-group">
                                        <label className="d-flex align-items-center" style={{ gap: 8 }}>
                                            <input type="checkbox" checked={!!s.bracketPlan}
                                                onChange={(e) => this.tocar('bracketPlan', e.target.checked ? presetPorKey('catan_oficial').values.bracketPlan : null)} />
                                            Formato por cantidad de anotados (bases Catan: semifinal de 16 desde 29)
                                        </label>
                                        {s.bracketPlan ? (
                                            <small className="text-muted">
                                                12–28: {describirEtapas(s.roundsPlanned, s.bracketPlan.tiers[0].stages)} ·
                                                29–79: {describirEtapas(s.roundsPlanned, s.bracketPlan.tiers[1].stages)} ·
                                                80+: {describirEtapas(s.roundsPlanned, s.bracketPlan.tiers[2].stages)}.
                                                El tramo se fija al generar la ronda 1.
                                            </small>
                                        ) : null}
                                    </div>
                                )}
                            </div>
                        ) : null}

                        {s.error ? (
                            <div style={{ background: '#FDECEA', color: '#8B2A20', borderRadius: '.25rem', padding: '8px 10px', fontSize: 13, marginBottom: 12 }}>
                                {s.error}
                            </div>
                        ) : null}
                        <div className="d-flex align-items-center" style={{ gap: 12 }}>
                            <button type="button" className="btn btn-accent" disabled={s.saving} onClick={this.crear}>
                                {s.saving ? 'Creando…' : 'Crear y configurar'}
                            </button>
                            <span className="text-muted" style={{ fontSize: 12.5 }}>
                                Los participantes se cargan enseguida, en Configuración. Todo lo de acá se puede ajustar ahí antes de empezar.
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}

export default TournamentCreateModal;
